import { Chart, ScatterDataPoint, ChartConfiguration, ChartDataset } from "chart.js";

import { PropType, defineComponent, shallowRef } from 'vue';
import { NPopover, NSpin } from "naive-ui";

import ComponentBase from "~/components/component-base";
import { backgroundColorLighter } from '~/variables';

import { baseChartConfig } from '~/components/chart/chart-base.config';
import { blueColor, greenColor } from "~/variables";
import { defaultsDeep } from "lodash";
import { Station } from "~/api/models/station.model";
import { AvoltaHumidityIndicator, AvoltaIndicator, AvoltaTemperatureIndicator } from "~/configs/indicators/avolta-indicators.config";
import { $t } from "~/i18n";
import { Moment } from "moment";

export default defineComponent({

    extends: ComponentBase,
    
    components: {
        NPopover,
        NSpin,
    },

    props: {
        station: { type: Object as PropType<Station>, required: true },
        measures: { type: Array as PropType<Measure[]>, required: true},
        since: { type: Object as PropType<Moment>, required: true},
        before: { type: Object as PropType<Moment>, required: true},
    },

    computed: {

        timeUnit(): 'day' | 'hour' | 'minute' {

            if(!this.before) {
                return 'day';
            }

            const delta = this.before.diff(this.since);
            if(delta >  3 * 24 * 60 * 60 * 1000) {
                return 'day';
            } else if(delta >  2 * 60 * 60 * 1000) {
                return 'hour';
            } else {
                return 'minute';
            }
        },

        timeUnitDisplayFormat(): { day: string, hour: string, minute: string} {

            if(!this.before) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }

            const delta = this.before.diff(this.since);
            if(delta >=  7 * 24 * 60 * 60 * 1000) {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            } else if(delta >=  3 * 24 * 60 * 60 * 1000){
                return { day: 'lll', hour: 'lll',  minute: 'HH:mm'};
            } else {
                return { day: 'll', hour: 'lll',  minute: 'HH:mm'};
            }
        },

        datasets() {
            const station = this.station;

            if (!station || !this.measures) {
                return [];
            }

            const temperatureData: ScatterDataPoint[] = this.measures.map(m => ({ x: m.date * 1000, y: m?.values[AvoltaIndicator.temperature] }));
            const humidityData: ScatterDataPoint[] = this.measures.map(m => ({ x: m.date * 1000, y: m?.values[AvoltaIndicator.humidity] }));

            const temperatureColor = AvoltaTemperatureIndicator.color || greenColor;
            const humidityColor = AvoltaHumidityIndicator.color || blueColor;
            
            return [
                {
                    label: `${this.$t('indicators.temperature.label')} (${this.$t('indicators.temperature.unit')})`,
                    data: temperatureData.map(sp => ({x: sp.x , y: sp.y})),
                    borderColor: temperatureColor,
                    backgroundColor: temperatureColor,
                    yAxisID: 'temperature',
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: temperatureData && temperatureData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                    tension: 0.3,
                } as ChartDataset,
                {
                    label: `${this.$t('indicators.humidity.label')} (${this.$t('indicators.humidity.unit')})`,
                    data: humidityData,
                    borderColor: humidityColor,
                    backgroundColor: humidityColor,
                    yAxisID: 'humidity',
                    barTickness: 20,
                    borderWidth: 1,
                    pointRadius: humidityData && humidityData.length <= 300 ? 0 : 0,
                    pointHitRadius: 10,
                    tension: 0.3,
                }
            ];
        }
    },

    data() {
        return {
            chart: null,
            config: defaultsDeep(
                {
                    options: {
                        interaction: {
                            mode: 'index',
                            intersect: false,
                          },
                        stacked: false,
                        scales: {
                            temperature: {
                                type: 'linear',
                                position: 'left',
                                display: true,

                                suggestedMin: 10,
                                suggestedmax: 40,

                                grid: {
                                    display: true,
                                    color: backgroundColorLighter,
                                },

                                title: {
                                    display: true,
                                    align: 'center',
                                    text: $t('indicators.temperature.unit'),
                                    color: 'white',
                                },
                                
                            },
                            humidity: {
                                type: 'linear',
                                display: true,
                                position: 'right',

                                suggestedMin: 10,
                                suggestedmax: 90,

                                
                                grid: {
                                    display: true,
                                    color: backgroundColorLighter,
                                    drawOnChartArea: false,
                                },

                                title: {
                                    display: true,
                                    align: 'center',
                                    text: $t('indicators.humidity.unit'),
                                    color: 'white',
                                }
                            },
                            y:undefined,
                        },
                    },
                },
                baseChartConfig(''),
            ) as ChartConfiguration,
            loading: false,
        };
    },

    mounted() {
        const chartElement: HTMLCanvasElement = this.$refs?.chart as HTMLCanvasElement;
        if (!chartElement || !this.since || !this.before) {
            return;
        }

        const chartConfig = this.config;
        chartConfig.data = { datasets: this.datasets};

        //@ts-ignore
        chartConfig.options.scales.x.time.unit = this.timeUnit;
        chartConfig.options.scales.x.suggestedMin = this.since.unix() * 1000;
        chartConfig.options.scales.x.suggestedMax = this.before.unix() * 1000;
        chartConfig.options.scales.x.time.displayFormats = this.timeUnitDisplayFormat;
        this.chart = shallowRef(new Chart(chartElement.getContext('2d'), chartConfig));
    },

    methods: {
        updateChart() {
            try {
                this.chart.data = { datasets: this.datasets};
                this.chart.update();
            } catch (e) {
                console.error(e);
            }
        },
    },

    watch: {
        datasets(datasets, old) {
            if(datasets === old) {
                return;
            }

            this.updateChart();
        }
    }
});